import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

export default function WizardGuide() {
  const [showHelpModal, setShowHelpModal] = useState(false);
  return (
    <>
      {/* Simple help text version */}
      <div className="mb-2 w-full rounded-md bg-blue-50 p-3">
        <div className="flex items-start">
          <InformationCircleIcon className="mt-0.5 size-5 shrink-0 text-blue-400" />
          <div className="ml-2 flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-blue-800">
                How to use Variables
              </h3>
              <button
                type="button"
                onClick={() => setShowHelpModal(true)}
                className="text-xs font-medium text-blue-600 underline hover:text-blue-800"
                data-testid="wizard-help-view-details-button"
              >
                View details
              </button>
            </div>
            <div className="mt-1 text-sm text-blue-700">
              <p className="mb-1">
                Use{' '}
                <code className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-xs">
                  {'{variableName}'}
                </code>{' '}
                format in VLAN IDs, descriptions, IP addresses, or subnet masks.
              </p>
              <ul className="ml-4 list-disc space-y-0.5 text-xs">
                <li>
                  <strong>Range:</strong> Set numeric range (e.g., 1-5) that
                  increments for each file
                </li>
                <li>
                  <strong>List:</strong> One value per line, no leading/trailing
                  spaces
                </li>
                <li>
                  Number of values must match the number of files to export
                </li>
                <li>
                  <strong>File naming:</strong> Each exported zip file will be
                  named as{' '}
                  <code className="rounded bg-blue-100 px-1 py-0.5 font-mono">
                    {'{projectName}-{firstVariableValue}.zip'}
                  </code>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed help modal */}
      <Dialog
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        className="relative z-50"
        data-testid="wizard-help-modal"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel
              transition
              className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <div className="px-6 pb-4 pt-6">
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-lg font-semibold text-gray-900">
                    How to use Variables - Detailed Guide
                  </DialogTitle>
                  <button
                    type="button"
                    onClick={() => setShowHelpModal(false)}
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    data-testid="wizard-help-modal-close-button"
                  >
                    <XMarkIcon className="size-6" />
                  </button>
                </div>
              </div>
              <div className="px-6 pb-6">
                <div className="space-y-4 text-sm text-gray-700">
                  <div>
                    <h4 className="mb-2 font-semibold text-gray-900">
                      Basic Usage
                    </h4>
                    <p className="mb-2">
                      Add variables to your configuration by using{' '}
                      <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                        {'{variableName}'}
                      </code>{' '}
                      format in VLAN IDs, descriptions, IP addresses, or subnet
                      masks. Variable names must be alphanumeric (letters and
                      numbers only).
                    </p>
                  </div>

                  <div>
                    <h4 className="mb-2 font-semibold text-gray-900">
                      Range Configuration
                    </h4>
                    <ul className="ml-4 list-disc space-y-1">
                      <li>
                        Set a numeric range (e.g., From: 1, To: 5) that will
                        increment sequentially for each exported file
                      </li>
                      <li>
                        The range must equal the number of files to export (To -
                        From + 1 = Number of files)
                      </li>
                      <li>
                        Example: Range 1-3 with 3 files will generate values: 1,
                        2, 3
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="mb-2 font-semibold text-gray-900">
                      List Configuration
                    </h4>
                    <ul className="ml-4 list-disc space-y-1">
                      <li>
                        Provide values in a text area, with{' '}
                        <strong>one value per line</strong>
                      </li>
                      <li>
                        <strong>Important:</strong> No leading or trailing
                        whitespace - values are automatically trimmed
                      </li>
                      <li>
                        The number of values must exactly match the number of
                        files to export
                      </li>
                      <li>Empty lines or empty values are not allowed</li>
                      <li>
                        Example for 3 files:
                        <pre className="mt-2 rounded bg-gray-100 p-2 text-xs">
                          {'value1\nvalue2\nvalue3'}
                        </pre>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="mb-2 font-semibold text-gray-900">
                      File Naming When Exporting
                    </h4>
                    <ul className="ml-4 list-disc space-y-1">
                      <li>
                        Each exported zip file will be named using the format:{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          {'{projectName}-{firstVariableValue}.zip'}
                        </code>
                      </li>
                      <li>
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          projectName
                        </code>{' '}
                        can contain variables that will be resolved for each
                        file
                      </li>
                      <li>
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          firstVariableValue
                        </code>{' '}
                        is the value of the first variable (in alphabetical
                        order) for that specific file
                      </li>
                      <li>
                        Example: If project name is{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          deployment
                        </code>{' '}
                        and first variable{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          x
                        </code>{' '}
                        has values 1, 2, 3, you will get:{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          deployment-1.zip
                        </code>
                        ,{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          deployment-2.zip
                        </code>
                        ,{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          deployment-3.zip
                        </code>
                      </li>
                      <li>
                        If project name contains variables like{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          site-{'{y}'}
                        </code>
                        , and{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          y
                        </code>{' '}
                        resolves to{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          A
                        </code>
                        , the file will be{' '}
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                          site-A-{'{firstVariableValue}'}.zip
                        </code>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="mb-2 font-semibold text-gray-900">
                      Important Notes
                    </h4>
                    <ul className="ml-4 list-disc space-y-1">
                      <li>
                        Reusing the same variable name applies the same value
                        everywhere it appears (e.g. 192.168.{'{v1}'}.{'{v1}'})
                      </li>
                      <li>
                        Use different names (e.g. {'{octet1}'}, {'{octet2}'})
                        when you need different value ranges
                      </li>
                      <li>
                        All variables must be configured (status must be
                        success) before exporting
                      </li>
                      <li>
                        Variables can be used in: VLAN ID, VLAN Description, IP
                        Address, Subnet Mask, Project Name
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-md bg-blue-50 p-3">
                    <h4 className="mb-2 font-semibold text-blue-900">
                      Complete Example
                    </h4>
                    <p className="text-sm text-blue-800">
                      Use{' '}
                      <code className="rounded bg-blue-100 px-1.5 py-0.5 font-mono">
                        192.168.{'{x}'}.1
                      </code>{' '}
                      in IP field, then configure variable{' '}
                      <code className="rounded bg-blue-100 px-1.5 py-0.5 font-mono">
                        x
                      </code>{' '}
                      with range 1-3 to generate 3 files with IPs: 192.168.1.1,
                      192.168.2.1, 192.168.3.1
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setShowHelpModal(false)}
                  className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  data-testid="wizard-help-modal-close-button-bottom"
                >
                  Got it
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}
